/**
 * The older dialogs' shared style kit (tag, region and work order dialogs, the
 * service picker), on the design system: colours are the shared semantic
 * tokens (`tokenVars`), text the shared text roles (`textRole`), controls the
 * ds field look (`fieldControlStyle`). Only the keys still in use are kept; new code
 * builds from the ds pieces (components/ui/ds) instead of reading these.
 */
import type React from 'react';
import { compactVars, sectionSpec, textRole, tokenVars } from '@turn-connect/ui';
import { fieldControlStyle, fieldLabelStyle } from '../ui/ds/FormField';

const card: React.CSSProperties = {
  background: tokenVars.surface,
  border: `1px solid ${tokenVars.border}`,
  borderRadius: sectionSpec.radius,
  padding: sectionSpec.body.padding,
  transition: 'background-color 150ms ease-out, border-color 150ms ease-out',
};

const statusBadge: React.CSSProperties = {
  ...textRole('pill'),
  display: 'inline-flex',
  alignItems: 'center',
  height: 24,
  padding: '0 8px',
  borderRadius: 999,
};

export const dashboardStyles = {
  card,
  cardHover: { background: tokenVars.surfaceRaised, border: `1px solid ${tokenVars.borderStrong}` } as React.CSSProperties,
  cardClickable: { cursor: 'pointer' } as React.CSSProperties,
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  } as React.CSSProperties,
  cardTitle: { ...textRole('cardTitle'), margin: 0 } as React.CSSProperties,
  cardSubtitle: { ...textRole('meta'), margin: '4px 0 0 0' } as React.CSSProperties,

  form: { display: 'grid', gap: 16 } as React.CSSProperties,
  formGroup: { display: 'flex', flexDirection: 'column', gap: 6 } as React.CSSProperties,
  label: { ...fieldLabelStyle, marginBottom: 6 } as React.CSSProperties,
  // 40px on a computer, 44px on a phone (the compact control height grows to 44 there).
  input: { ...fieldControlStyle(), minHeight: `max(40px, ${compactVars.controlHeight})`, outline: 'none' } as React.CSSProperties,
  textarea: { ...fieldControlStyle(), minHeight: 80, resize: 'vertical', outline: 'none' } as React.CSSProperties,

  flexRow: { display: 'flex', alignItems: 'center' } as React.CSSProperties,
  gap8: { gap: 8 } as React.CSSProperties,
  mb8: { marginBottom: 8 } as React.CSSProperties,
  mb16: { marginBottom: 16 } as React.CSSProperties,

  // Status words on a tinted ground, as the ds StatusPill.
  statusBadge,
  statusActive: { background: tokenVars.successSubtle, color: tokenVars.text.primary } as React.CSSProperties,
  statusInactive: { background: tokenVars.dangerSubtle, color: tokenVars.dangerText } as React.CSSProperties,
  statusPending: { background: tokenVars.warningSubtle, color: tokenVars.warningText } as React.CSSProperties,

  loadingSpinner: {
    ...textRole('meta'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
  } as React.CSSProperties,
  errorMessage: {
    ...textRole('body', tokenVars.dangerText),
    padding: '8px 12px',
    background: tokenVars.dangerSubtle,
    borderLeft: `3px solid ${tokenVars.danger}`,
    borderRadius: 6,
  } as React.CSSProperties,

  metricValue: { ...textRole('figure'), margin: '8px 0' } as React.CSSProperties,
  metricLabel: textRole('label'),
} as const;

/** Merges style objects left to right (falsy entries are skipped). */
export const combineStyles = (...styles: Array<React.CSSProperties | false | null | undefined | 0 | ''>): React.CSSProperties =>
  Object.assign({}, ...styles.filter(Boolean));

/**
 * Inject required field styles into the document.
 * Call this once in your component to add required field CSS.
 */
export const injectRequiredFieldStyles = () => {
  if (typeof document === 'undefined') return;

  const styleId = 'required-field-styles';

  // Remove old style element if exists to ensure fresh styles
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.remove();
  }

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Required field validation styles - only for error state */
    .required-field-error input,
    .required-field-error select,
    .required-field-error textarea {
      border: 2px solid ${tokenVars.danger} !important;
      box-shadow: 0 0 0 1px ${tokenVars.dangerSubtle} !important;
    }

    .required-field-error > div {
      border: 2px solid ${tokenVars.danger} !important;
    }
  `;
  document.head.appendChild(style);
};
